/**
 * @fileoverview Main formula resolution orchestrator
 * 
 * Coordinates the entire formula resolution process:
 * 1. Validates JoyDoc structure and extracts formulas
 * 2. Builds dependency graph to determine evaluation order
 * 3. Evaluates formulas in topological order
 * 4. Updates field values and collects warnings
 * 
 * Designed to be fault-tolerant: individual formula failures don't crash
 * the entire process, allowing partial form functionality.
 */

import { generateObjectId } from '@builttocreate/engine-utils';


import {
  CircularDependencyError,
  UnknownFieldError,
  FormulaEvalError,
  UnknownFunctionError,
  UndefinedVariableError,
} from './errors.js';
import { buildGraph } from './depGraph.js';
import { evalFormula } from './evaluator.js';
import { BUILTIN_NAMES } from './builtins.js';
import { validateFormula } from './validator.js';

// Field types that do NOT support formula write operations (read-only)
// Based on user requirements: signature, image, file, table, collection are read-only
// All other field types (including test-specific ones) allow formula writes
const FORMULA_READ_ONLY_FIELD_TYPES = new Set([
  'signature',
  'image', 
  'file',
  'table',
  'collection'
]);

/**
 * Validates JoyDoc structure and extracts formula information
 * 
 * @param {Object} doc - JoyDoc to validate
 * @returns {Object} Validation result
 * @returns {boolean} returns.isValid - Whether the document structure is valid
 * @returns {Array} returns.formulas - Extracted formulas array
 * @returns {string} returns.error - Error message if validation failed
 */
function validateDocumentStructure(doc) {
  if (!doc || typeof doc !== 'object') {
    return { isValid: false, error: 'Document must be an object' };
  }

  if (!Array.isArray(doc.fields)) {
    return { isValid: false, error: 'Document must have a fields array' };
  }

  // Support both 'formula' and 'formulas' array names for backward compatibility
  const formulas = doc.formulas || doc.formula || [];
  if (!Array.isArray(formulas)) {
    return { isValid: false, error: 'Formulas must be an array' };
  }

  return { isValid: true, formulas };
}

/**
 * Creates lookup maps for efficient field and formula access
 * 
 * @param {Array} fields - Array of field objects
 * @param {Array} formulas - Array of formula objects
 * @returns {Object} Lookup maps
 * @returns {Map} returns.fieldMap - Map of fieldId -> field object
 * @returns {Map} returns.formulaDefinitionMap - Map of formulaId -> formula definition
 * @returns {Array} returns.fieldFormulaApplications - Array of field formula applications to process
 * @returns {Array} returns.warnings - Any validation warnings found
 */
function createLookupMaps(fields, formulas) {
  const fieldMap = new Map();
  const formulaDefinitionMap = new Map();
  const fieldFormulaApplications = [];
  const warnings = [];

  // Build field lookup map
  for (const field of fields) {
    if (!field._id) {
      warnings.push(new FormulaEvalError('Field missing _id property', 'unknown'));
      continue;
    }
    fieldMap.set(field._id, field);
  }

  // Build formula definition lookup map
  for (const formula of formulas) {
    if (!formula._id || !formula.expression) {
      warnings.push(new FormulaEvalError('Formula missing _id or expression property', formula._id || 'unknown'));
      continue;
    }
    formulaDefinitionMap.set(formula._id, formula);
  }

  // Find field formula applications
  for (const field of fields) {
    if (!field.formulas || !Array.isArray(field.formulas)) {
      continue; // Field has no formulas applied
    }

    for (const appliedFormula of field.formulas) {
      if (!appliedFormula._id || !appliedFormula.formula || !appliedFormula.key) {
        warnings.push(new FormulaEvalError(
          `Applied formula missing required properties (_id, formula, key)`, 
          appliedFormula._id || 'unknown'
        ));
        continue;
      }

      // Check if referenced formula definition exists
      const formulaDefinition = formulaDefinitionMap.get(appliedFormula.formula);
      if (!formulaDefinition) {
        warnings.push(new UnknownFieldError(
          `Applied formula references unknown formula: ${appliedFormula.formula}`,
          appliedFormula._id
        ));
        continue;
      }

      // Add to processing list
      fieldFormulaApplications.push({
        fieldId: field._id,
        field: field,
        appliedFormula: appliedFormula,
        formulaDefinition: formulaDefinition,
        // Create unique key for dependency tracking
        applicationId: `${field._id}.${appliedFormula.key}`,
        expression: formulaDefinition.expression
      });
    }
  }

  return { fieldMap, formulaDefinitionMap, fieldFormulaApplications, warnings };
}

/**
 * Creates a context object with current field values for formula evaluation
 * 
 * @param {Map} fieldMap - Map of fieldId -> field object
 * @returns {Object} Context object with fieldId -> value mappings
 */
function createEvaluationContext(fieldMap) {
  const context = {};
  
  for (const [fieldId, field] of fieldMap) {
    // Use existing value or default to 0 for numeric fields
    let value = field.value;
    if (value === undefined || value === null) {
      // Preserve null for date fields, default to 0 for number, empty string for others
      if (field.type === 'date') {
        value = null;
      } else if (field.type === 'number') {
        value = 0;
      } else {
        value = '';
      }
    }
    
    // Handle dropdown fields: convert option ID to option value text
    if (field.type === 'dropdown' && field.options && Array.isArray(field.options)) {
      const selectedOption = field.options.find(option => option._id === value && !option.deleted);
      if (selectedOption && selectedOption.value !== undefined) {
        value = selectedOption.value;
      } else {
        value = ''; // Default to empty string if no valid option found
      }
    }
    
    // Handle multiSelect fields: convert array of option IDs to array of option text values
    if (field.type === 'multiSelect' && field.options && Array.isArray(field.options) && Array.isArray(value)) {
      value = value.map(optionId => {
        const selectedOption = field.options.find(option => option._id === optionId && !option.deleted);
        return selectedOption && selectedOption.value !== undefined ? selectedOption.value : '';
      }).filter(optionValue => optionValue !== ''); // Remove empty strings from deleted options
    }
    
    // Handle table fields: transform for formula evaluation
    if (field.type === 'table' && Array.isArray(value) && field.tableColumns) {
      // Transform table rows for formula evaluation
      value = value
        .filter(row => !row.deleted) // Filter out deleted rows
        .map(row => {
          // Create a new row object with cell values as direct properties
          const transformedRow = { ...row };
          
          // Add cell values as direct properties for easier access in formulas
          if (row.cells && typeof row.cells === 'object') {
            for (const [cellKey, cellValue] of Object.entries(row.cells)) {
              // Find the column definition to understand the field type
              const column = field.tableColumns.find(col => col._id === cellKey);
              
              if (column) {
                let transformedValue = cellValue;
                
                // Handle dropdown cells: convert option ID to option value text
                if (column.type === 'dropdown' && column.options && Array.isArray(column.options)) {
                  const selectedOption = column.options.find(option => option._id === cellValue && !option.deleted);
                  if (selectedOption && selectedOption.value !== undefined) {
                    transformedValue = selectedOption.value;
                  } else {
                    transformedValue = ''; // Default to empty string if no valid option found
                  }
                }
                
                // Handle multiSelect cells: convert array of option IDs to array of option text values
                if (column.type === 'multiSelect' && column.options && Array.isArray(column.options) && Array.isArray(cellValue)) {
                  transformedValue = cellValue.map(optionId => {
                    const selectedOption = column.options.find(option => option._id === optionId && !option.deleted);
                    return selectedOption && selectedOption.value !== undefined ? selectedOption.value : '';
                  }).filter(optionValue => optionValue !== ''); // Remove empty strings from deleted options
                }
                
                // Add the cell value as a direct property on the row for formula access
                transformedRow[cellKey] = transformedValue;
              }
            }
          }
          
          return transformedRow;
        });
    }
    
    // Handle chart fields: preserve structure for formula evaluation
    if (field.type === 'chart' && Array.isArray(value)) {
      // Chart fields already have the correct structure for formula access
      // Just ensure we're working with non-deleted data
      value = value.filter(line => !line.deleted).map(line => ({
        ...line,
        points: Array.isArray(line.points) ? line.points.filter(point => !point.deleted) : []
      }));
    }
    
    // Handle collection fields: transform for formula evaluation
    if (field.type === 'collection' && Array.isArray(value) && field.schema) {
      /**
       * Recursively transforms a collection row and its nested children
       * @param {Object} row - The collection row to transform
       * @param {string} schemaDepthKey - The schema key for this depth level
       * @returns {Object} Transformed row with cell values as direct properties
       */
      function transformCollectionRow(row, schemaDepthKey) {
        if (!row || typeof row !== 'object') return row;
        
        // Create a new row object with cell values as direct properties
        const transformedRow = { ...row };
        
        // Get the schema for this depth level
        const schema = field.schema[schemaDepthKey];
        
        // Add cell values as direct properties for easier access in formulas
        if (row.cells && typeof row.cells === 'object') {
          // First, add all raw cell values as properties (preserves any cell keys that don't match column IDs)
          for (const [cellKey, cellValue] of Object.entries(row.cells)) {
            transformedRow[cellKey] = cellValue;
          }
        }
        
        // Then ensure all column IDs are available as properties
        if (schema && schema.tableColumns) {
          for (const column of schema.tableColumns) {
            if (column.deleted) continue;
            
            // Check if there's a cell with this column ID (for CollectionField-style templates)
            let cellValue = undefined;
            if (row.cells && row.cells.hasOwnProperty(column._id)) {
              cellValue = row.cells[column._id];
            }
            
            // If no cell found with column ID, use default value based on type
            if (cellValue === undefined) {
              // Provide default value based on column type
              switch (column.type) {
                case 'number':
                  cellValue = undefined; // Will be undefined, formula should handle this
                  break;
                case 'text':
                case 'dropdown':
                  cellValue = '';
                  break;
                case 'multiSelect':
                  cellValue = [];
                  break;
                case 'date':
                  cellValue = null;
                  break;
                default:
                  cellValue = undefined;
              }
              // Add column ID as property with default value
              transformedRow[column._id] = cellValue;
            } else {
              // Cell value exists, transform it based on type
              let transformedValue = cellValue;
              
              // Handle dropdown cells: convert option ID to option value text
              if (column.type === 'dropdown' && column.options && Array.isArray(column.options)) {
                const selectedOption = column.options.find(option => option._id === cellValue && !option.deleted);
                if (selectedOption && selectedOption.value !== undefined) {
                  transformedValue = selectedOption.value;
                } else {
                  transformedValue = ''; // Default to empty string if no valid option found
                }
              }
              
              // Handle multiSelect cells: convert array of option IDs to array of option text values
              if (column.type === 'multiSelect' && column.options && Array.isArray(column.options) && Array.isArray(cellValue)) {
                transformedValue = cellValue.map(optionId => {
                  const selectedOption = column.options.find(option => option._id === optionId && !option.deleted);
                  return selectedOption && selectedOption.value !== undefined ? selectedOption.value : '';
                }).filter(optionValue => optionValue !== ''); // Remove empty strings from deleted options
              }
              
              // Add/override the cell value as a direct property using column ID
              transformedRow[column._id] = transformedValue;
            }
          }
        }
        
        // Transform nested children recursively
        if (row.children && typeof row.children === 'object') {
          transformedRow.children = {};
          
          for (const [childSchemaKey, childStructure] of Object.entries(row.children)) {
            // Handle both structures: direct array or {value: [...]} wrapper
            let childRows;
            
            if (Array.isArray(childStructure)) {
              // Direct array structure (legacy)
              childRows = childStructure;
            } else if (childStructure && typeof childStructure === 'object' && Array.isArray(childStructure.value)) {
              // Wrapped structure with "value" property
              childRows = childStructure.value;
            } else {
              // Other structure, preserve as-is
              transformedRow.children[childSchemaKey] = childStructure;
              continue;
            }
            
            transformedRow.children[childSchemaKey] = childRows
              .filter(childRow => !childRow.deleted) // Filter out deleted rows
              .map(childRow => transformCollectionRow(childRow, childSchemaKey));
          }
        }
        
        return transformedRow;
      }
      
      // Transform collection rows for formula evaluation
      // Find the root schema to start transformation
      const rootSchemaKey = Object.keys(field.schema).find(key => field.schema[key].root);
      const schemaDepthKey = rootSchemaKey || 'schemaDepth1'; // fallback to common naming
      
      value = value
        .filter(row => !row.deleted) // Filter out deleted rows
        .map(row => transformCollectionRow(row, schemaDepthKey));
    }
    
    context[fieldId] = value;
  }
  
  return context;
}

/**
 * Validates and normalizes a chart field value structure
 * Ensures all lines and points have valid _id fields
 * 
 * @param {any} value - The raw value from formula evaluation
 * @returns {Object} - Normalized chart value with validation result
 * @returns {Array} returns.value - The normalized array of lines
 * @returns {Array} returns.warnings - Any validation warnings
 */
function normalizeChartFieldValue(value) {
  const warnings = [];
  
  // Ensure value is an array
  if (!Array.isArray(value)) {
    warnings.push('Chart field formula must return an array of lines');
    return { value: [], warnings };
  }
  
  // Process each line
  const normalizedValue = value.map((line, lineIndex) => {
    // Ensure line is an object
    if (!line || typeof line !== 'object') {
      warnings.push(`Line at index ${lineIndex} must be an object`);
      return null;
    }
    
    // Create normalized line
    const normalizedLine = {
      _id: line._id || generateObjectId(),
      title: line.title || '',
      description: line.description || '',
      points: []
    };
    
    // Ensure points is an array
    if (!Array.isArray(line.points)) {
      warnings.push(`Line at index ${lineIndex} must have a points array`);
      return normalizedLine;
    }
    
    // Process each point
    normalizedLine.points = line.points.map((point, pointIndex) => {
      // Ensure point is an object
      if (!point || typeof point !== 'object') {
        warnings.push(`Point at index ${pointIndex} in line ${lineIndex} must be an object`);
        return null;
      }
      
      // Create normalized point
      const normalizedPoint = {
        _id: point._id || generateObjectId(),
        label: point.label !== undefined ? String(point.label) : '',
        x: typeof point.x === 'number' ? point.x : (parseFloat(point.x) || 0),
        y: typeof point.y === 'number' ? point.y : (parseFloat(point.y) || 0)
      };
      
      return normalizedPoint;
    }).filter(point => point !== null); // Remove invalid points
    
    return normalizedLine;
  }).filter(line => line !== null); // Remove invalid lines
  
  return { value: normalizedValue, warnings };
}

/**
 * Converts a formula evaluation result to the appropriate type for the target field
 * 
 * @param {any} result - The raw result from formula evaluation
 * @param {string} fieldType - The type of the target field (e.g., 'text', 'number', 'date')
 * @param {string} fieldId - The ID of the field being updated (for error messages)
 * @param {Object} field - The field object being updated (for dropdown option lookup)
 * @returns {Object} - The converted result with any warnings
 * @returns {any} returns.value - The converted value appropriate for the field type
 * @returns {Array} returns.warnings - Any conversion warnings
 */
function convertResultForFieldType(result, fieldType, fieldId = 'unknown', field = null) {
  const warnings = [];
  
  // Handle null/undefined results
  if (result === null || result === undefined) {
    // Preserve null for date fields, use appropriate defaults for others
    let defaultValue;
    switch (fieldType) {
      case 'date':
        defaultValue = null;
        break;
      case 'number':
        defaultValue = 0;
        break;
      case 'chart':
        defaultValue = [];
        break;
      default:
        defaultValue = '';
    }
    return { 
      value: defaultValue,
      warnings
    };
  }
  
  switch (fieldType) {
    case 'text':
      // For text fields, convert everything to string representation
      let textValue;
      if (typeof result === 'boolean') {
        textValue = result.toString(); // 'true' or 'false'
      } else if (typeof result === 'number') {
        textValue = result.toString();
      } else if (typeof result === 'string') {
        textValue = result;
      } else {
        // For objects/arrays, convert to JSON string
        try {
          textValue = JSON.stringify(result);
        } catch {
          textValue = String(result);
        }
      }
      return { value: textValue, warnings };
      
    case 'number':
      // For number fields, try to convert to number
      let numberValue;
      if (typeof result === 'number') {
        numberValue = result;
      } else if (typeof result === 'boolean') {
        numberValue = result ? 1 : 0;
      } else if (typeof result === 'string') {
        const parsed = parseFloat(result);
        numberValue = isNaN(parsed) ? 0 : parsed;
      } else {
        numberValue = 0;
      }
      return { value: numberValue, warnings };
      
    case 'date':
      // For date fields, expect timestamp or date
      let dateValue;
      if (result === null || result === undefined) {
        dateValue = null; // Preserve null/undefined for date fields
      } else if (typeof result === 'number') {
        dateValue = result; // Assume it's already a timestamp
      } else if (result instanceof Date) {
        dateValue = result.getTime();
      } else if (typeof result === 'string') {
        const parsed = new Date(result);
        // If can't parse the string, return null instead of current date
        dateValue = isNaN(parsed.getTime()) ? null : parsed.getTime();
      } else {
        // For other types, return null instead of current date
        dateValue = null;
      }
      return { value: dateValue, warnings };
      
    case 'dropdown':
      // For dropdown fields, convert text value back to option ID
      if (typeof result === 'string' && field && field.options && Array.isArray(field.options)) {
        // Empty string remains empty string (no selection)
        if (result === '') {
          return { value: '', warnings };
        }
        
        // Find the option with matching text value (case-sensitive match)
        const matchingOption = field.options.find(option => 
          !option.deleted && option.value === result
        );
        
        if (matchingOption) {
          // Return the option ID
          return { value: matchingOption._id, warnings };
        } else {
          // If no matching option found, return the text value as-is (will be invalid)
          // This allows the UI to handle invalid values appropriately
          warnings.push(`Dropdown field '${fieldId}': No option found with value "${result}"`);
          return { value: result, warnings };
        }
      }
      // If not a string or no options available, return as-is
      return { value: result, warnings };
      
    case 'multiSelect':
      // For multiSelect fields, convert array of text values back to array of option IDs
      if (Array.isArray(result) && field && field.options && Array.isArray(field.options)) {
        const convertedValues = [];
        
        for (const textValue of result) {
          // Skip empty strings and non-string values
          if (typeof textValue !== 'string' || textValue === '') {
            continue;
          }
          
          // Find the option with matching text value (case-sensitive match)
          const matchingOption = field.options.find(option => 
            !option.deleted && option.value === textValue
          );
          
          if (matchingOption) {
            // Add the option ID
            convertedValues.push(matchingOption._id);
          }
          // Silently ignore values that don't have matching options
          // This is the expected behavior for multiSelect fields
        }
        
        return { value: convertedValues, warnings };
      }
      // If not an array or no options available, ensure we return an array
      if (!Array.isArray(result)) {
        warnings.push(`MultiSelect field '${fieldId}': Formula must return an array`);
        return { value: [], warnings };
      }
      return { value: result, warnings };
      
    case 'chart':
      // For chart fields, validate and normalize the structure
      const chartResult = normalizeChartFieldValue(result);
      if (chartResult.warnings.length > 0) {
        chartResult.warnings.forEach(warning => {
          warnings.push(`Chart field '${fieldId}': ${warning}`);
        });
      }
      return { value: chartResult.value, warnings };
      
    default:
      // For other field types, return the result as-is
      return { value: result, warnings };
  }
}

/**
 * Extracts field IDs referenced in a formula expression
 * Uses regex pattern matching to find identifier-like tokens, then filters
 * to only include tokens that match actual field IDs in the document.
 * 
 * @param {string} expression - Formula string like "sum(fieldA, fieldB) + fieldC"
 * @param {Set<string>} validFieldIds - All field IDs that exist in the document
 * @returns {Set<string>} - Set of field IDs that this formula depends on
 */
function extractFieldDependencies(expression, validFieldIds) {
  if (!expression || typeof expression !== 'string') {
    return new Set();
  }

  // Keywords to ignore (JavaScript literals that aren't field references)
  // Note: 'if' is no longer in this list since it's now a valid builtin function
  const jsKeywords = new Set([
    'true', 'false', 'null', 'undefined', 'NaN', 'Infinity',
    'let', 'const', 'var', 'function', 'return', 'else'
  ]);

  const dependencies = new Set();

  // First, find property access chains (e.g., "table1.0.text1" or "table1[0].text1")
  // These should only create dependencies on the base field (e.g., "table1")
  const propertyAccessPattern = /([a-zA-Z_$][a-zA-Z0-9_$]*)(?:\.[a-zA-Z0-9_$]+|\[[^\]]+\])+/g;
  let match;
  let processedRanges = [];

  while ((match = propertyAccessPattern.exec(expression)) !== null) {
    const baseField = match[1]; // Extract the base field name
    
    // Skip if it's a built-in function, JavaScript keyword, or not a valid field ID
    if (BUILTIN_NAMES.has(baseField) || jsKeywords.has(baseField) || !validFieldIds.has(baseField)) {
      continue;
    }
    
    dependencies.add(baseField);
    // Track this range so we don't double-process it as a simple identifier
    processedRanges.push({ start: match.index, end: match.index + match[0].length });
  }

  // Then, find simple identifiers that aren't part of property access chains
  const identifierPattern = /[a-zA-Z_$][a-zA-Z0-9_$]*/g;
  
  while ((match = identifierPattern.exec(expression)) !== null) {
    const token = match[0];
    const matchStart = match.index;
    const matchEnd = match.index + token.length;
    
    // Skip if this identifier is part of a property access chain we already processed
    const isPartOfPropertyAccess = processedRanges.some(range => 
      matchStart >= range.start && matchEnd <= range.end
    );
    
    if (isPartOfPropertyAccess) {
      continue;
    }
    
    // Skip if it's a built-in function, JavaScript keyword, or not a valid field ID
    if (BUILTIN_NAMES.has(token) || jsKeywords.has(token) || !validFieldIds.has(token)) {
      continue;
    }
    
    dependencies.add(token);
  }

  return dependencies;
}

/**
 * Performs topological sort on a dependency graph with cycle detection
 * 
 * @param {Map<string, Set<string>>} dependencyMap - Map of nodeId -> Set of dependencies
 * @param {string[]} targetNodes - Nodes we want in the final evaluation order
 * @returns {string[]} - Nodes in topological order
 * @throws {CircularDependencyError} When a circular dependency is detected
 */
function performTopologicalSort(dependencyMap, targetNodes) {
  const visiting = new Set();    // Gray nodes (currently being processed)
  const visited = new Set();     // Black nodes (fully processed)
  const topologicalOrder = [];   // Result array

  /**
   * Depth-first search for topological ordering
   */
  function depthFirstSearch(nodeId, currentPath) {
    // Already processed this node
    if (visited.has(nodeId)) return;
    
    // Found a cycle - node is already being processed
    if (visiting.has(nodeId)) {
      const cycleStart = currentPath.indexOf(nodeId);
      const cycle = [...currentPath.slice(cycleStart), nodeId];
      throw new CircularDependencyError(cycle);
    }

    // Mark as currently being processed
    visiting.add(nodeId);
    currentPath.push(nodeId);

    // Process all dependencies first
    const dependencies = dependencyMap.get(nodeId) || new Set();
    for (const dependency of dependencies) {
      depthFirstSearch(dependency, [...currentPath]);
    }

    // Finished processing this node
    currentPath.pop();
    visiting.delete(nodeId);
    visited.add(nodeId);
    
    // Add to result (reverse post-order gives us topological order)
    topologicalOrder.push(nodeId);
  }

  // Process each target node
  for (const nodeId of targetNodes) {
    if (!visited.has(nodeId)) {
      depthFirstSearch(nodeId, []);
    }
  }

  // Return only the target nodes in topological order
  return topologicalOrder.filter(nodeId => targetNodes.includes(nodeId));
}

/**
 * Resolves all formulas in a JoyDoc by evaluating them in dependency order
 * 
 * This is the main entry point for formula resolution. It handles the complete
 * workflow from validation through evaluation, working on a copy of the document
 * to avoid modifying the original.
 * 
 * @param {Object} doc - JoyDoc containing fields and formulas
 * @returns {Object} Resolution result
 * @returns {Object} returns.doc - New document with resolved formula values (original unchanged)
 * @returns {Array} returns.warnings - Array of warning/error objects
 * @returns {string} returns.status - 'valid', 'warning', or 'error'
 */
export function resolveJoyDoc(doc) {
  const warnings = [];

  // Step 0: Create a deep copy of the document to avoid modifying the original
  const docCopy = JSON.parse(JSON.stringify(doc));

  // Step 1: Validate document structure
  const validation = validateDocumentStructure(docCopy);
  if (!validation.isValid) {
    throw new Error(`Invalid JoyDoc structure: ${validation.error}`);
  }

  const { formulas } = validation;

  // Step 2: Create lookup maps and validate formulas
  const { fieldMap, formulaDefinitionMap, fieldFormulaApplications, warnings: lookupWarnings } = createLookupMaps(docCopy.fields, formulas);
  warnings.push(...lookupWarnings);

  // Short-circuit if no valid formulas to process
  if (fieldFormulaApplications.length === 0) {
    return { 
      doc: docCopy, 
      warnings,
      status: warnings.length > 0 ? 'warning' : 'valid'
    };
  }

  // Step 3: Build dependency graph and determine evaluation order
  let evaluationOrder = [];
  const circularNodes = new Set(); // Track nodes involved in circular dependencies
  
  // Build custom dependency graph that handles applicationId -> fieldId dependencies
  const dependencyMap = new Map();
  const allNodeIds = new Set(); // Includes both applicationIds and fieldIds
  
  // Add all applicationIds as nodes
  for (const app of fieldFormulaApplications) {
    allNodeIds.add(app.applicationId);
    dependencyMap.set(app.applicationId, new Set());
  }
  
  // Add all fieldIds as nodes  
  for (const fieldId of fieldMap.keys()) {
    allNodeIds.add(fieldId);
    dependencyMap.set(fieldId, new Set());
  }
  
  // Build dependencies: applicationId -> fieldIds it depends on
  for (const app of fieldFormulaApplications) {
    const fieldDeps = extractFieldDependencies(app.expression, new Set(fieldMap.keys()));
    for (const fieldDep of fieldDeps) {
      dependencyMap.get(app.applicationId).add(fieldDep);
    }
  }
  
  // Add indirect dependencies: fieldId -> applicationIds that update it
  for (const app of fieldFormulaApplications) {
    const fieldId = app.fieldId;
    if (dependencyMap.has(fieldId)) {
      dependencyMap.get(fieldId).add(app.applicationId);
    }
  }
  
  // Try to detect circular dependencies and exclude them from evaluation
  const nonCircularApplications = [];
  for (const app of fieldFormulaApplications) {
    try {
      // Try to perform topological sort for just this application
      performTopologicalSort(dependencyMap, [app.applicationId]);
      nonCircularApplications.push(app.applicationId);
    } catch (error) {
      if (error instanceof CircularDependencyError) {
        // This application is part of a circular dependency
        warnings.push(error);
        // Mark all nodes in the cycle as circular
        if (error.cycle) {
          error.cycle.forEach(node => circularNodes.add(node));
        }
      } else {
        throw error;
      }
    }
  }
  
  // Perform topological sort only on non-circular applications
  if (nonCircularApplications.length > 0) {
    try {
      evaluationOrder = performTopologicalSort(dependencyMap, nonCircularApplications);
    } catch (error) {
      // This shouldn't happen since we already filtered out circular dependencies
      if (error instanceof CircularDependencyError) {
        warnings.push(error);
      } else {
        throw error;
      }
    }
  }

  // Step 4: Evaluate formulas in dependency order
  // Create a map of applicationId -> fieldFormulaApplication for quick lookup
  const applicationMap = new Map();
  for (const app of fieldFormulaApplications) {
    applicationMap.set(app.applicationId, app);
  }

  for (const applicationId of evaluationOrder) {
    const application = applicationMap.get(applicationId);

    if (!application) {
      continue; // Skip if application is missing
    }

    const { field, appliedFormula, formulaDefinition } = application;

    try {
      // Step 1: Validate the formula before evaluation
      const validationErrors = validateFormula(
        formulaDefinition.expression, 
        formulaDefinition._id, 
        new Set(fieldMap.keys())
      );
      
      // If validation errors found, add them as warnings and skip evaluation
      if (validationErrors.length > 0) {
        warnings.push(...validationErrors);
        continue;
      }

      // Step 2: Create fresh context with latest field values
      const context = createEvaluationContext(fieldMap);
      
      // Step 3: Evaluate the formula
      const result = evalFormula(formulaDefinition.expression, context, formulaDefinition._id);
      
      // Step 4: Convert result based on field type
      const conversionResult = convertResultForFieldType(result, field.type, field._id, field);
      
      // Add any conversion warnings
      if (conversionResult.warnings.length > 0) {
        conversionResult.warnings.forEach(warning => {
          warnings.push(new FormulaEvalError(warning, formulaDefinition._id));
        });
      }
      
      // Step 5: Check if field type is read-only (does not support formula write operations)
      if (FORMULA_READ_ONLY_FIELD_TYPES.has(field.type)) {
        // Skip updating read-only field types (signature, image, file, table, collection)
        warnings.push(new FormulaEvalError(
          `Field type '${field.type}' does not support formula write operations. Formula result was not applied to field '${field._id}'.`,
          formulaDefinition._id
        ));
        continue;
      }
      
      // Step 6: Update the specified field property (usually 'value')
      field[appliedFormula.key] = conversionResult.value;
      
      // Step 7: Update the field map so subsequent formulas see the new value
      fieldMap.set(field._id, field);
      
    } catch (error) {
      // Convert evaluation errors to warnings so other formulas can still run
      const warning = error instanceof Error ? error : new FormulaEvalError(String(error), formulaDefinition._id);
      warnings.push(warning);
    }
  }

  // Determine final status
  const hasErrors = warnings.some(w => w instanceof FormulaEvalError && !w.message.includes('missing'));
  const status = hasErrors ? 'warning' : warnings.length > 0 ? 'warning' : 'valid';

  return { 
    doc: docCopy, 
    warnings,
    status
  };
}